import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Stack
} from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ADMIN_MODULE_AUTH_BASE_PATH,
  ADMIN_MODULE_BASE_PATH,
  PERSONAL_DETAIL
} from "routing/Paths";
import {
  callToCloudFunction,
  getDataFromFirestore
} from "services/api/apiRequests";
import { CF_URLS } from "services/api/endpoints";
import spacing from "styles/spacing";
import {
  DEFAULT_LOCALE_CODE,
  DISTRICT_ID_GET_ALL_LOCATIONS,
  ERROR_TYPES,
  numberConstants
} from "utils/constants";
import { dataDogErrorLog, findErrorMessage, handleRefId } from "utils/helpers";
import { personnelDetailStyles } from "./PersonnelDetailStyles";
import PersonnelHistoryTable from "./PersonnelHistoryTable";
const PersonnelDetailHistory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { firstName, lastName } = location.state;
  const [historyData, setHistoryData] = useState([]);
  const [throwErrorMessage, setThrowErrorMessage] = useState(null);
  const [throwError, setThrowError] = useState(false);
  const [isResetGrid, setIsResetGrid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(numberConstants.one);
  const [totalRecords, setTotalRecords] = useState(numberConstants.zero);
  const [lastFetchedPage, setLastFetchedPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    parseInt(numberConstants.tweleve)
  );
  const [sortProperty, setSortProperty] = useState(numberConstants.zero);
  const [sortDirection, setSortDirection] = useState(numberConstants.zero);
  const [allCustomerList, setAllCustomerList] = useState([]);
  const [filteredCustomer, setFilteredCustomer] = useState(
    numberConstants.minusOne
  );

  let initialValues = {
    personnel: ""
  };

  const personalHistoryForm = useFormik({
    initialValues: initialValues
  });

  const handleBackButtonClick = () => {
    navigate(
      `${ADMIN_MODULE_BASE_PATH}/${ADMIN_MODULE_AUTH_BASE_PATH}/${PERSONAL_DETAIL}/${id}`
    );
  };

  useEffect(() => {
    getHistoryData();
    setLastFetchedPage(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    lastFetchedPage,
    rowsPerPage,
    sortProperty,
    sortDirection,
    filteredCustomer
  ]);

  const getResponseData = async (reqBody, url, resultSetCount) => {
    setThrowError(false);
    setThrowErrorMessage(null);
    try {
      const response = await callToCloudFunction(reqBody, url);
      const dataSets = await getDataFromFirestore(
        response,
        resultSetCount,
        response?.data?.docId
      );

      return dataSets;
    } catch (error) {
      let { refId, message } = handleRefId(findErrorMessage(ERROR_TYPES.ISSUE));
      dataDogErrorLog(
        message,
        url,
        {
          ...reqBody,
          error
        },
        refId
      );
      return null;
    }
  };

  const handleChangePage = (page) => {
    const nextPage = page + parseInt(numberConstants.one);
    setCurrentPage(nextPage);
  };

  const handleColumnSort = (property, direction) => {
    setSortProperty(property);
    setSortDirection(direction);
  };

  const handlePageRowsChange = (number, page) => {
    setCurrentPage(page + parseInt(numberConstants.one));
    setRowsPerPage(number);
  };
  const handleFilterValueChange = (filterValue) => {
    setCurrentPage(parseInt(numberConstants.one));
    setFilteredCustomer(filterValue);
  };

  const getHistoryData = async () => {
    setIsLoading(true);

    try {
      const startIndex =
        (currentPage - parseInt(numberConstants.one)) * parseInt(rowsPerPage) +
        parseInt(numberConstants.one);
      const endIndex =
        currentPage * parseInt(rowsPerPage) + parseInt(numberConstants.one);
      const reqBody = JSON.stringify({
        main_district_id: DISTRICT_ID_GET_ALL_LOCATIONS,
        personnel_id: id,
        iso_locale_code: DEFAULT_LOCALE_CODE,
        start_index: startIndex.toString(),
        end_index: endIndex.toString(),
        sort_property: sortProperty,
        sort_order: sortDirection,
        customer_number_filter: filteredCustomer
      });

      const response = await getResponseData(
        reqBody,
        CF_URLS.personnalHistory.getPersonalHistory,
        3
      );
      if (response) {
        if (response.error) {
          let { refId, message } = handleRefId(response.error);
          setThrowErrorMessage(message);
          setThrowError(true);
          dataDogErrorLog(
            message,
            CF_URLS.personnalHistory.getPersonalHistory,
            {
              ...reqBody,
              error: response
            },
            refId
          );
        } else {
          const dataSets = response.data;
          const newData = dataSets[0];
          if (startIndex.toString() === numberConstants.one) {
            const customerData = dataSets[2];
            const sortedCustomerList = customerData.sort((objA, objB) => {
              const a = objA.number;
              const b = objB.number;

              if (a === "") {
                return -1;
              }

              if (b === "") {
                return 1;
              }

              return a.localeCompare(b);
            });

            const customerNumberList = sortedCustomerList.map(
              (customer) => customer.number
            );
            setAllCustomerList(customerNumberList);
          }
          if (newData[0]?.error) {
            let { refId, message } = handleRefId(newData[0]?.error);
            setThrowErrorMessage(message);
            setThrowError(true);
            dataDogErrorLog(
              message,
              CF_URLS.personnalHistory.getPersonalHistory,
              {
                ...reqBody,
                error: message
              },
              refId
            );
          } else {
            newData.sort((a, b) => {
              const rowA = a.rownumber ? parseInt(a.rownumber) : 0;
              const rowB = b.rownumber ? parseInt(b.rownumber) : 0;

              if (rowA < rowB) {
                return -1;
              }
              if (rowA > rowB) {
                return 1;
              }
              return 0; // rowA must be equal to rowB
            });
            setTotalRecords(dataSets[1][0]?.total_rows);
            setHistoryData(newData);
            setIsResetGrid(false);
          }
        }
      } else {
        let { refId, message } = handleRefId(
          findErrorMessage(ERROR_TYPES.ISSUE)
        );
        setThrowErrorMessage(message);
        setThrowError(true);
        dataDogErrorLog(
          message,
          CF_URLS.personnalHistory.getPersonalHistory,
          {
            ...reqBody
          },
          refId
        );
      }
    } catch (error) {
      let errorMessage = error?.message || findErrorMessage(ERROR_TYPES.ISSUE);
      let { refId, message } = handleRefId(errorMessage);
      setThrowErrorMessage(message);
      setThrowError(true);
      dataDogErrorLog(
        message,
        CF_URLS.personnalHistory.getPersonalHistory,
        {
          error
        },
        refId
      );
    }
    setIsLoading(false);
  };

  const tableProps = {
    historyData,
    isResetGrid,
    isLoading,
    handleChangePage,
    totalRecords,
    personalHistoryForm,
    firstName,
    lastName,
    handlePageRowsChange,
    handleColumnSort,
    rowsPerPage,
    allCustomerList,
    handleFilterValueChange
  };
  return (
    <>
      {throwError && (
        <Collapse in={throwError}>
          <Alert
            severity="error"
            sx={{ marginBottom: spacing.margingTop20 }}
            action={
              <IconButton
                aria-label={t("common.close")}
                color="inherit"
                size="small"
                onClick={() => {
                  setThrowError(false);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {throwErrorMessage}
          </Alert>
        </Collapse>
      )}
      <Stack gap={spacing.gap}>
        <PersonnelHistoryTable {...tableProps} />
        <Divider />
        <Box sx={personnelDetailStyles.buttonStyles.containerBox}>
          <Button variant="outlined" onClick={handleBackButtonClick}>
            {t("common.back")}
          </Button>
        </Box>
      </Stack>
    </>
  );
};

export default PersonnelDetailHistory;
